import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  ArrowLeft,
  Filter,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CheckCircle,
  Truck,
  Mail,
  Brain,
  Sparkles,
  Layers,
  Star,
  Phone,
  MessageCircle
} from "lucide-react";

// Logo URL
const LOGO_URL = "/achraj_logo.png";

// Animated Grid Background Component
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-100 rounded-full blur-[100px]"></div>
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]"></div>
  </div>
);

// Indian States
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [memberCredits, setMemberCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Get category from URL parameter
  const selectedCategory = searchParams.get('category');
  
  // Checkout form
  const [formData, setFormData] = useState({
    member_id: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_address: "",
    city: "",
    state: "Uttar Pradesh",
    pincode: "",
    payment_mode: "cod",
    notes: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products${selectedCategory ? `?category_id=${selectedCategory}` : ""}`),
          axios.get(`${API}/categories`)
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Error:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedCategory]);

  const handleCategoryChange = (categoryId) => {
    window.scrollTo(0, 0);
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const getCategoryName = () => {
    if (!selectedCategory) return null;
    const cat = categories.find(c => c.id === selectedCategory);
    return cat?.name || null;
  };

  // Cart Functions
  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        price: product.price,
        point_value: product.point_value || 0,
        image_url: product.image_url,
        quantity: 1
      }]);
    }
    toast.success(`${product.name} added to cart!`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getTotalPV = () => {
    return cart.reduce((sum, item) => sum + ((item.point_value || 0) * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // Auto-fill checkout from saved Member details
  const fetchMemberDetails = async (memberId) => {
    const id = (memberId || "").trim().toUpperCase();
    if (id.length < 4) return;
    try {
      const res = await axios.get(`${API}/members/${id}/shipping`);
      const d = res.data;
      setMemberCredits(d.credits || 0);
      setFormData((prev) => ({
        ...prev,
        customer_name: d.name || prev.customer_name,
        customer_phone: d.phone || prev.customer_phone,
        customer_email: d.email || prev.customer_email,
        customer_address: d.address || prev.customer_address,
        city: d.city || prev.city,
        state: d.state || prev.state,
        pincode: d.pincode || prev.pincode,
      }));
      toast.success("Details auto-filled from your Member ID");
    } catch (err) {
      // Member not found yet — let them fill manually, no error toast
    }
  };

  // Checkout
  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (cart.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }

    if (!formData.member_id) {
      toast.error("Member ID is required to place orders");
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        ...formData,
        member_id: formData.member_id.toUpperCase(),
        items: cart,
        total_amount: getTotalAmount(),
        use_wallet: formData.payment_mode === "wallet"
      };

      const res = await axios.post(`${API}/orders`, orderData);
      setOrderSuccess(res.data);
      setCart([]);
      setCheckoutOpen(false);
      setFormData({
        member_id: "",
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        customer_address: "",
        city: "",
        state: "Uttar Pradesh",
        pincode: "",
        payment_mode: "cod",
        notes: ""
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="shop-page">
      <GridBackground />
      
      {/* Navigation - Light */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-orange-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="Achraj Logo" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-orange-200" />
              <div className="hidden sm:block">
                <span className="font-bold text-lg text-gray-900">Achraj Services</span>
                <p className="text-xs text-gray-500">Shop</p>
              </div>
            </Link>
            
            <Button 
              onClick={() => setCartOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-gray-900 rounded-full relative"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-5 h-5" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {getTotalItems()}
                </span>
              )}
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 pt-28 pb-16 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 rounded-full text-sm font-medium mb-4">
            <ShoppingCart className="w-4 h-4" /> Shop Products
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gray-900">Shop </span>
            <span className="text-orange-600 font-bold">
              {getCategoryName() || "All Products"}
            </span>
          </h1>
          <p className="text-gray-500 text-lg">Quality products delivered to your doorstep</p>
        </div>

        {/* Categories Filter */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          <Button
            variant={!selectedCategory ? "default" : "outline"}
            onClick={() => handleCategoryChange(null)}
            className={`rounded-full transition-all ${
              !selectedCategory 
                ? "bg-orange-500 text-gray-900 shadow-lg shadow-orange-200 border-0" 
                : "border-gray-300 text-gray-600 hover:border-cyan-500/50 hover:text-orange-600 bg-gray-50"
            }`}
          >
            <Filter className="w-4 h-4 mr-2" /> All
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              onClick={() => handleCategoryChange(cat.id)}
              className={`rounded-full transition-all ${
                selectedCategory === cat.id 
                  ? "bg-orange-500 text-gray-900 shadow-lg shadow-orange-200 border-0" 
                  : "border-gray-300 text-gray-600 hover:border-cyan-500/50 hover:text-orange-600 bg-gray-50"
              }`}
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-orange-300 border-t-cyan-500 rounded-full animate-spin"></div>
              <Brain className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-orange-600" />
            </div>
            <p className="text-gray-500 mt-4">Loading products...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product.id} className="group relative">
                <div className="absolute -inset-0.5 bg-orange-500 rounded-2xl blur opacity-0 group-hover:opacity-30 transition duration-500"></div>
                <Card className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden backdrop-blur-xl hover:border-orange-300 transition-all h-full">
                  <div className="aspect-square bg-gray-50 relative overflow-hidden">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center absolute inset-0 bg-gray-100 ${product.image_url ? 'hidden' : 'flex'}`}>
                      <Package className="w-16 h-16 text-gray-300" />
                    </div>
                    {product.stock === 0 && (
                      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center">
                        <span className="text-red-600 font-semibold border border-red-300 px-4 py-2 rounded-full bg-red-50">Out of Stock</span>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      {product.stock > 0 && (
                        <Badge className="bg-green-100 text-green-600 border border-green-200">
                          <Star className="w-3 h-3 mr-1" /> In Stock
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-5">
                    {(product.category_name || (product.category_id && !product.category_id.includes('-'))) && (
                      <Badge className="mb-3 bg-purple-500/10 text-purple-400 border border-purple-500/30 hover:bg-purple-500/20">
                        <Layers className="w-3 h-3 mr-1" />
                        {product.category_name || product.category_id}
                      </Badge>
                    )}
                    <h3 className="text-gray-900 font-semibold text-lg mb-2 line-clamp-1">{product.name}</h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-400">Price</p>
                        <p className="text-2xl font-bold text-orange-600 font-bold">₹{product.price}</p>
                        <span className="inline-block mt-1 text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" data-testid="product-pv-badge">{product.point_value || 0} PV</span>
                      </div>
                      {product.stock > 0 ? (
                        <Button 
                          size="sm"
                          onClick={() => addToCart(product)}
                          className="bg-orange-500  text-gray-900 rounded-full shadow-lg shadow-orange-200"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      ) : (
                        <span className="text-xs text-red-600">Out of Stock</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="relative inline-block mb-6">
              <div className="absolute -inset-4 bg-orange-100 rounded-full blur-xl"></div>
              <Package className="relative w-20 h-20 text-gray-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products available</h3>
            <p className="text-gray-500 mb-6">Check back soon!</p>
            {selectedCategory && (
              <Button 
                onClick={() => handleCategoryChange(null)}
                className="bg-orange-500 text-gray-900 rounded-full"
              >
                View All Products
              </Button>
            )}
          </div>
        )}
      </main>

      {/* Cart Drawer */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <ShoppingCart className="w-5 h-5 text-orange-600" /> Your Cart ({getTotalItems()} items)
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {cart.length > 0 ? (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex items-center gap-3 p-3 bg-gray-50/50 rounded-xl border border-gray-200">
                    {item.image_url && (
                      <img src={item.image_url} alt={item.product_name} className="w-16 h-16 object-cover rounded-lg" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900">{item.product_name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-orange-600 font-bold">₹{item.price}</p>
                        <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">{(item.point_value || 0) * item.quantity} PV</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" className="h-8 w-8 border-slate-600 text-gray-900 hover:bg-slate-700" onClick={() => updateQuantity(item.product_id, -1)}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button size="icon" variant="outline" className="h-8 w-8 border-slate-600 text-gray-900 hover:bg-slate-700" onClick={() => updateQuantity(item.product_id, 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-300 hover:bg-red-50" onClick={() => removeFromCart(item.product_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Your cart is empty</p>
              </div>
            )}
          </div>
          {cart.length > 0 && (
            <div className="border-t border-gray-300 pt-4 space-y-4">
              <div className="flex justify-between text-lg font-bold">
                <span className="text-gray-900">Total:</span>
                <span className="text-orange-600 font-bold">₹{getTotalAmount().toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-sm -mt-2">
                <span className="text-gray-500">Total Point Value (PV)</span>
                <span className="font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" data-testid="cart-total-pv">{getTotalPV()} PV</span>
              </div>
              <Button 
                onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                className="w-full bg-orange-500  text-gray-900 rounded-full"
                data-testid="checkout-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Proceed to Checkout
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Truck className="w-5 h-5 text-orange-600" /> Checkout
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCheckout} className="space-y-4">
            {/* Order Summary */}
            <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-200">
              <h4 className="font-semibold mb-2 text-gray-900">Order Summary</h4>
              <div className="space-y-2 text-sm">
                {cart.map(item => (
                  <div key={item.product_id} className="flex justify-between text-gray-600">
                    <span>{item.product_name} x {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-gray-300 pt-2 flex justify-between font-bold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-orange-600 font-bold">₹{getTotalAmount().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-purple-700 font-semibold">
                  <span>Total PV</span>
                  <span data-testid="checkout-total-pv">{getTotalPV()} PV</span>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="space-y-3">
              <div>
                <Label className="text-orange-600 font-semibold">Member ID * (Required)</Label>
                <Input
                  value={formData.member_id}
                  onChange={(e) => setFormData({...formData, member_id: e.target.value.toUpperCase()})}
                  onBlur={(e) => fetchMemberDetails(e.target.value)}
                  placeholder="Enter your Member ID (e.g., ACJ00001)"
                  required
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500 focus:border-cyan-500"
                />
                <p className="text-xs text-gray-400 mt-1">Only registered members can place orders — your saved details auto-fill below</p>
              </div>
              <div>
                <Label className="text-gray-600">Full Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  placeholder="Enter your full name"
                  required
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                />
              </div>
              <div>
                <Label className="text-gray-600">Phone Number *</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  placeholder="10-digit mobile number"
                  required
                  minLength={10}
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                />
              </div>
              <div>
                <Label className="text-gray-600">Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  placeholder="your@email.com"
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                />
              </div>
              <div>
                <Label className="text-gray-600">Delivery Address *</Label>
                <Textarea
                  value={formData.customer_address}
                  onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                  placeholder="House/Flat No., Street, Landmark"
                  required
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-600">City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="City"
                    required
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">Pincode *</Label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) => setFormData({...formData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                    placeholder="6-digit pincode"
                    required
                    minLength={6}
                    className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-600">State *</Label>
                <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-50 border-gray-300">
                    {INDIAN_STATES.map(state => (
                      <SelectItem key={state} value={state} className="text-gray-900 hover:bg-slate-700">{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-600">Payment Method *</Label>
                <Select value={formData.payment_mode} onValueChange={(v) => setFormData({...formData, payment_mode: v})}>
                  <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-50 border-gray-300">
                    <SelectItem value="cod" className="text-gray-900 hover:bg-slate-700">Cash on Delivery (COD)</SelectItem>
                    <SelectItem value="upi" className="text-gray-900 hover:bg-slate-700">UPI Payment</SelectItem>
                    <SelectItem value="bank" className="text-gray-900 hover:bg-slate-700">Bank Transfer</SelectItem>
                    <SelectItem value="wallet" className="text-gray-900 hover:bg-slate-700">Wallet Credits (₹{memberCredits})</SelectItem>
                  </SelectContent>
                </Select>
                {formData.payment_mode === "wallet" && (
                  <p className="text-xs text-[#E65100] mt-1" data-testid="shop-wallet-note">
                    ₹{getTotalAmount()} will be paid from your wallet (balance ₹{memberCredits}).
                  </p>
                )}
              </div>
              <div>
                <Label className="text-gray-600">Order Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special instructions..."
                  className="bg-gray-50 border-gray-300 text-gray-900 placeholder-slate-500"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-500  text-gray-900 py-6 rounded-full"
              disabled={submitting}
              data-testid="place-order-btn"
            >
              {submitting ? "Placing Order..." : `Place Order (₹${getTotalAmount().toLocaleString()})`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Order Success Dialog */}
      <Dialog open={!!orderSuccess} onOpenChange={() => setOrderSuccess(null)}>
        <DialogContent className="sm:max-w-md text-center bg-white border-gray-200">
          <div className="py-6">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-200">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Placed!</h2>
            <p className="text-gray-500 mb-4">Thank you for your order</p>
            
            {orderSuccess && (
              <div className="bg-gray-50/50 rounded-xl p-4 text-left mb-6 border border-gray-200">
                <p className="text-sm text-gray-500">Order ID</p>
                <p className="text-xl font-bold text-orange-600 font-bold">{orderSuccess.order_id}</p>
                <p className="text-sm text-gray-400 mt-2">
                  We will contact you at <strong className="text-gray-900">{orderSuccess.customer_phone}</strong> for order confirmation.
                </p>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
              <Mail className="w-4 h-4 text-orange-600" />
              <span>For support: <strong className="text-orange-600">achrajaigroup@gmail.com</strong></span>
            </div>

            <Button 
              onClick={() => setOrderSuccess(null)}
              className="bg-orange-500  text-gray-900 rounded-full"
            >
              Continue Shopping
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-[#050810] border-t border-slate-800 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 mb-2">Achraj Services Private Limited</p>
          <p className="text-gray-300 text-sm mb-4">Quality products, trusted service</p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="tel:+919956864243" className="flex items-center gap-2 text-gray-500 hover:text-orange-600 transition-colors">
              <Phone className="w-4 h-4" /> +91 99568 64243
            </a>
            <a href="mailto:achrajaigroup@gmail.com" className="flex items-center gap-2 text-gray-500 hover:text-orange-600 transition-colors">
              <Mail className="w-4 h-4" /> achrajaigroup@gmail.com
            </a>
            <a href="https://wa.me/919956864243?text=Hi%20Achraj%2C%20I%20need%20help%20with%20my%20order" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-500 hover:text-green-600 transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/919956864243?text=Hi%20Achraj%2C%20I%20need%20help%20with%20my%20order"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-gray-900 p-4 rounded-full shadow-lg shadow-green-500/30 transition-all hover:scale-110"
        data-testid="whatsapp-float-btn"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
